// modules/heroImg-carousel.js

/**
 * 使用 cloneNode 確保 Vue 綁定的屬性也能正確複製
 */
const splitImageForAnimation = (container, splits = 4) => {
	const originalImg = container.querySelector('img');

	// 防呆：找不到圖片或已經切過，直接回傳既有的
	if (!originalImg || container.querySelectorAll('.img-slice').length > 0) {
		return container.querySelectorAll('.img-slice');
	}

	// [重要] 先複製節點，確保拿到當下正確的圖片狀態 (包含 src)
	// 這樣就算 Vue 還沒完全渲染完屬性，我們也能拿到 DOM 當下的狀態
	const baseCloneImg = originalImg.cloneNode(true);

	// 確保複製出來的圖片樣式正確 (因為原圖稍後會被設為透明)
	baseCloneImg.style.opacity = '1';
	baseCloneImg.style.width = '100%';
	baseCloneImg.style.height = '100%';
	baseCloneImg.style.objectFit = 'contain'; // 確保比例不跑掉

	// 隱藏原始圖片 (保留佔位，確保父容器有寬高)
	gsap.set(originalImg, { opacity: 0 });

	const slices = [];
	for (let i = 0; i < splits; i++) {
		const wrapper = document.createElement('div');
		wrapper.classList.add('img-slice', 'absolute', 'inset-0', 'w-full', 'h-full');

		// 計算裁切範圍
		const leftClip = i * (100 / splits);
		const rightClip = 100 - ((i + 1) * (100 / splits));
		wrapper.style.clipPath = `inset(0% ${rightClip}% 0% ${leftClip}%)`;

		// 初始化狀態：隱藏
		gsap.set(wrapper, { autoAlpha: 0 });

		// 複製一份圖片放入 slice
		const clone = baseCloneImg.cloneNode(true);
		wrapper.appendChild(clone);

		container.appendChild(wrapper);
		slices.push(wrapper);
	}

	return slices;
};

export const heroCarousel = () => {
	if (typeof gsap === 'undefined') return;

	// --- 1. 參數設定 ---
	const CONFIG = {
		zoomDuration: 6,     // 背景縮放總時長 (比 4s 長，確保轉場不被切斷)
		fadeDuration: 1.5,     // 轉場淡入時長
		nextSlideDelay: 5.0,   // 第 5 秒開始切換下一張
		scaleTo: 1.15,         // 放大倍率
		logoStagger: 0.15      // 文字切片出現間隔
	};

	const getYOffset = () => window.innerWidth < 1201 ? 15 : 30;

	// --- 2. DOM 選取 ---
	const box1 = document.querySelector('.hero-img-box-1');
	const box2 = document.querySelector('.hero-img-box-2');

	// [防呆] 如果 Vue 還沒渲染出 DOM，直接結束，等待下一次呼叫
	if (!box1 || !box2) return;

	// 準備切片 (Logo 2)
	const logo2SlicesBox1 = splitImageForAnimation(box1.querySelector('.hero-logo-2'), 4);
	const logo2SlicesBox2 = splitImageForAnimation(box2.querySelector('.hero-logo-2'), 4);

	// 準備 Slide 物件陣列
	const slides = [
		{
			el: box1,
			bg: box1.querySelector('picture img'),
			logo1: box1.querySelector('.hero-logo-1'),
			logo2Slices: logo2SlicesBox1
		},
		{
			el: box2,
			bg: box2.querySelector('picture img'),
			logo1: box2.querySelector('.hero-logo-1'),
			logo2Slices: logo2SlicesBox2
		}
	];

	// --- 3. 初始狀態 Reset ---
	gsap.set('.hero-logobox', { zIndex: 20, perspective: 1000 });

	// 確保 Logo 2 容器是可見的 (因為裡面的切片預設是 hidden)
	gsap.set('.hero-logo-2', { autoAlpha: 1 });

	gsap.set([box1, box2], { opacity: 0, zIndex: 1 });
	gsap.set([slides[0].bg, slides[1].bg], { scale: 1 });

	// --- 4. 核心動畫函式 ---
	const playSlide = (index, isFirstRun = false) => {
		const currentSlide = slides[index];
		const nextIndex = (index + 1) % slides.length;

		// A. 層級設定
		gsap.set(currentSlide.el, { zIndex: 10 });
		slides.forEach((s, i) => {
			if (i !== index) gsap.set(s.el, { zIndex: 1 });
		});

		// B. 建立時間軸
		const tl = gsap.timeline();

		// 1. 圖片 Scale (5s)
		tl.fromTo(currentSlide.bg,
			{ scale: 1 },
			{ scale: CONFIG.scaleTo, duration: CONFIG.zoomDuration, ease: "none" },
			0
		);

		// 2. 容器 Fade In (1s)
		tl.fromTo(currentSlide.el,
			{ opacity: isFirstRun ? 1 : 0 },
			{ opacity: 1, duration: isFirstRun ? 0 : CONFIG.fadeDuration, ease: "power1.inOut" },
			0
		);

		// 3. Logo 1 動畫 (第 1 秒出現，持續 2.5 秒)
		tl.fromTo(currentSlide.logo1,
			{ autoAlpha: 0, rotationY: 0 },
			{
				autoAlpha: 1,
				rotationY: 360,
				duration: 2.5,
				ease: "power2.out"
			},
			1.3
		);

		// 4. Logo 2 動畫 (第 2.5 秒出現，持續 1.7 秒)
		// [檢查點] 確保有切片才執行動畫
		if (currentSlide.logo2Slices && currentSlide.logo2Slices.length > 0) {
			tl.fromTo(currentSlide.logo2Slices,
				{ autoAlpha: 0, y: getYOffset() },
				{
					autoAlpha: 1,
					y: 0,
					duration: 1.7,
					ease: "power2.out",
					stagger: CONFIG.logoStagger
				},
				2.8
			);
		}

		// D. 排程下一張 (第 4 秒)
		gsap.delayedCall(CONFIG.nextSlideDelay, () => {
			playSlide(nextIndex, false);
		});
	};

	// --- 5. 啟動 ---
	playSlide(0, true);
};